<script setup>
import { computed, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { io } from 'socket.io-client'
import { WS_URL } from '../config.js'
import { fetchEventInfo, fetchSplits } from '../utils/checkpoint-data.js'
import {
  FINISH_TYPES,
  START_TYPES,
  buildCheckpointRows,
  markerFullLabel,
  markerType,
  passTimesForMarker,
  sortCheckpointRows
} from '../utils/checkpoint-results.js'
import { formatClock, formatDuration, formatGap, parsePunchTime } from '../utils/polish-time.js'

// Ekran punktu kontrolnego (jak /finish): kto wszedł na punkt, godzina, czas od startu, miejsce na punkcie.
// Odbicia RFID przychodzą od razu przez websocket (`new_punch` z marker_id); pozostałe źródła
// (GPS, SportIdent, ręczne) — z /splits, odświeżanego co REFRESH_MS (API ma cache 15 s).
const REFRESH_MS = 15000
const HIGHLIGHT_MS = 8000

const route = useRoute()
const eventAlias = route.params.eventAlias
const markerId = String(route.params.markerId)

const data = ref(null)
const eventInfo = ref(null)
const error = ref('')
const connected = ref(false)
const order = ref('latest')
const livePasses = ref(new Map()) // runner_id → ms, z websocketu (zanim /splits je pokaże)
const highlighted = ref(new Set())
let knownRunnerIds = null // po pierwszym wczytaniu: kto już był na punkcie
let socket = null
let refreshTimer = null
const highlightTimers = new Map()

const marker = computed(() => data.value?.markers.find(m => String(m.id) === markerId) || null)
const title = computed(() => marker.value ? markerFullLabel(marker.value) : `Punkt ${markerId}`)

const passTimes = computed(() => {
  const passes = passTimesForMarker(data.value?.splits, markerId)
  for (const [runnerId, ms] of livePasses.value) {
    const prev = passes.get(runnerId)
    if (prev == null || ms < prev) passes.set(runnerId, ms)
  }
  return passes
})

const rows = computed(() => {
  if (!data.value) return []
  return buildCheckpointRows({
    runners: data.value.runners,
    passTimes: passTimes.value,
    eventStartMs: eventInfo.value?.startMs ?? null,
    categories: data.value.categories
  })
})

const sortedRows = computed(() => sortCheckpointRows(rows.value, order.value))
const hasCategories = computed(() => rows.value.some(r => r.categoryPlace != null && r.categoryName))
const startersCount = computed(() => (data.value?.runners || []).filter(r => !Number(r.abs)).length)

function highlight (runnerId) {
  const key = String(runnerId)
  highlighted.value = new Set(highlighted.value).add(key)
  clearTimeout(highlightTimers.get(key))
  highlightTimers.set(key, setTimeout(() => {
    const next = new Set(highlighted.value)
    next.delete(key)
    highlighted.value = next
    highlightTimers.delete(key)
  }, HIGHLIGHT_MS))
}

async function load () {
  try {
    data.value = await fetchSplits(eventAlias)
    error.value = ''
  } catch (e) {
    error.value = 'Nie udało się odświeżyć wyników — próbuję dalej.'
    return
  }
  const ids = new Set(passTimes.value.keys())
  if (knownRunnerIds) {
    for (const id of ids) if (!knownRunnerIds.has(id)) highlight(id)
  }
  knownRunnerIds = ids
}

// Odbicie RFID: punkt po marker_id; meta/start bywają przypisane bez punktu (mode finish/start).
function punchMatchesMarker (punch) {
  if (punch.marker_id != null && String(punch.marker_id) === markerId) return true
  if (punch.marker_id != null || !marker.value) return false
  const type = markerType(marker.value)
  return (punch.mode === 'finish' && FINISH_TYPES.includes(type)) ||
    (punch.mode === 'start' && START_TYPES.includes(type))
}

function handlePunch (punch) {
  if (!punch || !punchMatchesMarker(punch)) return
  const ms = parsePunchTime(punch.punch_timestamp || punch.timestamp)
  if (ms == null || punch.runner_id == null) return
  const key = String(punch.runner_id)
  const prev = passTimes.value.get(key)
  if (prev != null && prev <= ms) return // już był na punkcie (pętla) — liczy się pierwsze wejście
  livePasses.value = new Map(livePasses.value).set(key, ms)
  if (knownRunnerIds) knownRunnerIds.add(key)
  highlight(key)
}

function connectSocket (eventId) {
  socket = io(WS_URL)
  socket.on('connect', () => {
    connected.value = true
    socket.emit('join_event', eventId)
  })
  socket.on('disconnect', () => { connected.value = false })
  socket.on('connect_error', (err) => console.error('Websocket connect_error:', err.message))
  socket.on('new_punch', handlePunch)
}

fetchEventInfo(eventAlias)
  .then(info => {
    eventInfo.value = info
    if (info.id != null) connectSocket(info.id)
  })
  .catch(() => { error.value = 'Nie udało się pobrać wydarzenia.' })
load()
refreshTimer = setInterval(load, REFRESH_MS)

onUnmounted(() => {
  clearInterval(refreshTimer)
  for (const timer of highlightTimers.values()) clearTimeout(timer)
  if (socket) {
    socket.disconnect()
    socket = null
  }
})
</script>

<template>
  <div class="checkpoint-view">
    <router-link :to="{ name: 'Checkpoints', params: { eventAlias } }" class="small">← Wszystkie punkty</router-link>
    <h1 class="mt-1 mb-1">{{ title }}</h1>
    <p v-if="eventInfo" class="text-muted mb-2">{{ eventInfo.name }}</p>

    <div class="toolbar mb-2">
      <span :class="['badge', connected ? 'bg-success' : 'bg-danger']">
        {{ connected ? 'Na żywo' : 'Brak połączenia' }}
      </span>
      <span class="text-muted small">Na punkcie: <strong>{{ rows.length }}</strong> / {{ startersCount }}</span>
      <div class="btn-group btn-group-sm ms-auto" role="group" aria-label="Kolejność">
        <button type="button" :class="['btn', order === 'latest' ? 'btn-primary' : 'btn-outline-primary']" @click="order = 'latest'">Najnowsi</button>
        <button type="button" :class="['btn', order === 'ranking' ? 'btn-primary' : 'btn-outline-primary']" @click="order = 'ranking'">Ranking</button>
      </div>
    </div>

    <div v-if="error" class="alert alert-warning py-1 small">{{ error }}</div>
    <div v-if="!data" class="text-muted">Wczytywanie…</div>
    <div v-else-if="!marker" class="alert alert-warning">Nie ma takiego punktu z międzyczasami w tym wydarzeniu.</div>
    <div v-else-if="rows.length === 0" class="text-muted">Nikt jeszcze nie przeszedł przez ten punkt.</div>

    <div v-else class="checkpoint-feed">
      <div
        v-for="row in sortedRows"
        :key="row.id"
        :class="['checkpoint-item', { 'checkpoint-item--new': highlighted.has(String(row.id)) }]"
      >
        <div class="runner-info">
          <span class="number-badge" :style="row.categoryColor ? { backgroundColor: row.categoryColor } : null">{{ row.number }}</span>
          <div class="name-block">
            <span class="name">{{ row.name }}<span v-if="row.bio" class="bio"> ({{ row.bio }})</span></span>
            <div class="details">
              <span>Wejście: {{ formatClock(row.passMs) }}</span>
              <span v-if="row.categoryName">{{ row.categoryName }}</span>
              <span v-if="row.dnf" class="badge bg-secondary">DNF</span>
            </div>
          </div>
        </div>
        <div class="result-block">
          <div class="result-times">
            <span class="result-time">{{ formatDuration(row.fromStartMs) }}</span>
            <span v-if="row.gapMs" class="gap">{{ formatGap(row.gapMs) }}</span>
          </div>
          <span v-if="row.place" class="badge place-badge">{{ row.place }}.</span>
          <span
            v-if="hasCategories && row.categoryPlace"
            class="badge category-badge"
            :style="row.categoryColor ? { backgroundColor: row.categoryColor } : null"
            :title="row.categoryName"
          >{{ row.categoryPlace }}</span>
          <span v-if="row.genderPlace" class="badge gender-badge">{{ row.gender }} {{ row.genderPlace }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.checkpoint-view {
  width: 100%;
  text-align: left;
}

@media (min-width: 768px) {
  .checkpoint-view {
    max-width: 720px;
    margin: 0 auto;
  }
}

.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.checkpoint-feed {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.checkpoint-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  padding: 0.6rem 0.9rem;
  border: 1px solid var(--sledz-border-soft);
  border-radius: 0.5rem;
  background: var(--sledz-surface);
}

.checkpoint-item--new {
  background: var(--sledz-highlight);
  font-weight: 600;
  animation: highlight-row 1.5s ease-out;
}

.runner-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  flex: 1 1 220px;
}

.number-badge {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2.75rem;
  height: 2.75rem;
  padding: 0 0.3rem;
  border-radius: 1.4rem;
  background: var(--sledz-bib-badge);
  color: var(--sledz-panel-text);
  font-size: 1.15rem;
  font-weight: 700;
}

.name-block {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.name {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bio {
  font-weight: 400;
  color: var(--sledz-text-muted);
}

.details {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.8rem;
  color: var(--sledz-text-muted);
}

.result-block {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 0 0 auto;
}

.result-times {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.1;
}

.result-time {
  font-size: 1.4rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.gap {
  font-size: 0.8rem;
  color: var(--sledz-text-muted);
  font-variant-numeric: tabular-nums;
}

.place-badge {
  background: var(--sledz-brand);
  color: var(--sledz-brand-ink);
  font-size: 0.9rem;
}

.category-badge {
  background: var(--sledz-panel);
  color: var(--sledz-panel-text);
  font-size: 0.85rem;
}

.gender-badge {
  background: var(--sledz-surface-alt);
  color: var(--sledz-text);
  border: 1px solid var(--sledz-border);
  font-size: 0.85rem;
}

@keyframes highlight-row {
  0% { background: var(--sledz-highlight-flash); }
  100% { background: var(--sledz-highlight); }
}
</style>
